import React from 'react';
import { ArrowLeft, Shield, Lock, Cloud, Server, CheckCircle, AlertCircle, BarChart3, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const SovereignAI = () => {
  const features = [
    {
      icon: Lock,
      title: "Zero Data Retention",
      description: "Model providers cannot retain or access your proprietary data. Complete data sovereignty guaranteed."
    },
    {
      icon: Cloud,
      title: "VPC Deployment",
      description: "LLMs deployed exclusively within your Virtual Private Cloud using AWS Nitro Enclaves or Azure Confidential Computing."
    },
    {
      icon: Server,
      title: "Kubernetes Auto-scaling",
      description: "Containerized inference with automatic scaling based on demand, ensuring optimal performance and cost efficiency."
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Military-grade encryption, SOC 2 Type II compliance, and comprehensive audit trails for all operations."
    }
  ];

  const models = [
    {
      name: "Llama 3.1 405B",
      type: "Open Source",
      description: "State-of-the-art open source model with 405B parameters, optimized for financial analysis."
    },
    {
      name: "Mistral Large",
      type: "Commercial",
      description: "High-performance commercial model with strong reasoning capabilities for complex financial tasks."
    },
    {
      name: "GPT-4 via Azure",
      type: "Private Link",
      description: "Access GPT-4 through Azure Private Link, ensuring data never leaves your infrastructure."
    }
  ];

  const compliance = [
    "SOC 2 Type II Certified",
    "GDPR Compliant",
    "ISO 27001:2013",
    "FINRA Compliant",
    "PCI DSS Level 1",
    "HIPAA Ready"
  ];

  return (
    <div className="pt-16 min-h-screen bg-navy-950 text-white">
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <Link to="/" className="inline-flex items-center text-orange-400 hover:text-orange-300 mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
            <div>
              <div className="inline-flex p-3 rounded-2xl bg-gradient-to-r from-[#f97316] to-[#fbce16] mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-5">
                <span className="text-gradient-blue">Sovereign AI Infrastructure</span>
              </h1>
              <h2 className="text-xl sm:text-2xl text-slate-300 mb-6">
                Beyond the Public Cloud: Private LLM Orchestration
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                Financial institutions cannot risk "Data Leakage" to public model trainers. Our sovereign AI
                infrastructure ensures your prompts and proprietary data never touch the public internet.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/#demo"
                  className="inline-flex items-center justify-center px-7 py-3 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:scale-[1.02]"
                >
                  Request Demo
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-7 py-3 border border-slate-700/50 text-white font-semibold rounded-lg hover:bg-white/5 transition-all duration-300"
                >
                  View All Solutions
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#f97316]/20 to-[#fbce16]/20 rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/40 shadow-2xl shadow-orange-900/20">
                <img
                  src="/solution1_privatellm.png"
                  alt="Sovereign AI infrastructure"
                  className="w-full h-auto object-cover max-h-[340px] sm:max-h-[420px] lg:max-h-[520px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/45 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-2xl p-8 mb-16 border border-slate-700/40 premium-border premium-elevate">
            <div className="flex items-start space-x-4">
              <AlertCircle className="w-8 h-8 text-orange-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-bold mb-4 text-white">The Problem</h3>
                <p className="text-lg text-slate-300 leading-relaxed">
                  Financial institutions handle sensitive customer data, proprietary trading algorithms, 
                  and confidential strategic information. Using public AI services creates unacceptable 
                  risks of data leakage, regulatory violations, and competitive disadvantage.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-2xl p-8 mb-16 border border-slate-700/40 premium-border premium-elevate">
            <div className="flex items-start space-x-4">
              <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-bold mb-4 text-white">The BeeMind Solution</h3>
                <p className="text-lg text-slate-300 leading-relaxed mb-6">
                  We deploy state-of-the-art LLMs (Llama 3.1 405B, Mistral Large, or GPT-4 via Azure Private Link) 
                  exclusively within your Virtual Private Cloud (VPC). By utilizing AWS Nitro Enclaves or Azure 
                  Confidential Computing, we ensure that your prompts and proprietary data never touch the public internet.
                </p>
                <div className="bg-black/50 rounded-lg p-6 border border-slate-700/40 premium-subcard premium-elevate">
                  <p className="text-xl font-semibold text-green-400 mb-2">Key Advantage:</p>
                  <p className="text-2xl font-bold text-white">0% Data Retention by model providers</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Core Features</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 premium-border premium-elevate hover:border-orange-500 transition-all duration-300">
                    <div className="flex items-start space-x-4">
                      <div className="inline-flex p-3 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold mb-2 text-white">{feature.title}</h4>
                        <p className="text-slate-400 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Supported Models</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {models.map((model, index) => (
                <div key={index} className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 premium-border premium-elevate hover:border-orange-500 transition-all duration-300">
                  <div className="mb-4">
                    <h4 className="text-lg font-bold text-white">{model.name}</h4>
                    <p className="text-slate-400 text-sm">{model.provider}</p>
                  </div>
                  <div className="space-y-2">
                    {(model.features || []).map((feature, idx) => (
                      <p key={idx} className="text-slate-400 text-sm">{feature}</p>
                    ))}
                  </div>
                  <p className="text-slate-400 leading-relaxed">{model.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Compliance & Security</h3>
            <div className="bg-navy-800 rounded-xl p-8 border border-slate-700/40 premium-border premium-elevate">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {compliance.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-3xl font-bold mb-8">Ready to Deploy Sovereign AI?</h3>
            <p className="text-xl text-slate-400 mb-8">
              Schedule a consultation with our enterprise solutions team to discuss your specific requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#demo"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:scale-105"
              >
                Schedule Consultation
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center px-8 py-4 border border-slate-700/40 text-white font-semibold rounded-lg hover:bg-navy-800 transition-all duration-300"
              >
                View Other Services
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SovereignAI;
